import { useCallback, useEffect, useMemo, useState } from 'react'
import { Grid, GridColumn, GridCellProps, GridHeaderCellProps, GridSortChangeEvent } from '@progress/kendo-react-grid'
import { orderBy, SortDescriptor } from '@progress/kendo-data-query'
import { DropDownList } from '@progress/kendo-react-dropdowns'
import { Switch } from '@progress/kendo-react-inputs'
import { FaCheck, FaChevronDown, FaChevronRight, FaMinus, FaSearch, FaSyncAlt, FaTimes } from 'react-icons/fa'
import {
  getNotifiableWorksOverview,
  JobNotifiableWork,
  JobNotifiableWorkDocument,
  NotifiableWorkCatalogueEntry,
  NotifiableWorkJobRow,
  NotifiableWorkOverview,
} from '../../data/notifiableWorkData'
import { getDocument } from '../../data/fileData'
import { useToast } from '../../contexts/ToastContext'
import { getApiErrorDetails } from '../../helpers/apiError'
import './NotifiableWorks.css'

type View = 'job' | 'work'

const PAGE_SIZE = 25

/** "Complete" here means nothing required is outstanding. */
const workState = (work: JobNotifiableWork) =>
  work.requiredCount === 0 ? 'none' : work.outstanding === 0 ? 'done' : 'outstanding'

const openJob = (jobId: number) => window.open(`/jobs?id=${jobId}`, '_blank')

/**
 * The attributes Kendo's own cells carry. Without k-table-td a custom cell
 * loses the theme's padding, and its content sits flush left of its header.
 */
const tdProps = (props: GridCellProps, extraClass?: string) => ({
  className: ['k-table-td', props.className, extraClass].filter(Boolean).join(' '),
  style: props.style,
  colSpan: props.colSpan,
  role: 'gridcell',
  'aria-colindex': props.ariaColumnIndex,
  'aria-selected': props.isSelected,
  'data-grid-col-index': props.columnIndex,
})

/** A flat row for the grid: Kendo sorts on top-level fields. */
interface JobGridRow extends NotifiableWorkJobRow {
  /** The chosen work on this job, in the by-notifiable-work view. */
  work?: JobNotifiableWork
  /** Sort key for the done column. */
  workDone?: number
}

const DocumentStatusIcon = ({ document }: { document: JobNotifiableWorkDocument }) =>
  document.actioned ? (
    <span className='nwo-mark nwo-mark--done' title='Done'>
      <FaCheck aria-label='Done' />
    </span>
  ) : (
    <span
      className={`nwo-mark ${document.isRequired ? 'nwo-mark--missing' : 'nwo-mark--optional'}`}
      title={document.isRequired ? 'Required, not done' : 'Optional, not done'}
    >
      <FaTimes aria-label='Not done' />
    </span>
  )

/**
 * Notifiable works across every job: by job, each job's works with how many of
 * their required documents are done; or by notifiable work, every job with
 * that work and the state of each of its documents. Shows incomplete ones -
 * a required document still to do - unless Show complete is on.
 */
const NotifiableWorks = () => {
  const { showToast } = useToast()

  const [data, setData] = useState<NotifiableWorkOverview | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [view, setView] = useState<View>('job')
  const [showComplete, setShowComplete] = useState(false)
  const [search, setSearch] = useState('')
  const [selectedWorkId, setSelectedWorkId] = useState<number | null>(null)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [sort, setSort] = useState<SortDescriptor[]>([])
  const [skip, setSkip] = useState(0)

  const load = useCallback(async () => {
    setLoading(true)
    setLoadError(null)
    try {
      setData(await getNotifiableWorksOverview())
    } catch (error) {
      const message = getApiErrorDetails(error, "The notifiable works couldn't be loaded.").message
      setLoadError(message)
      showToast(message, 'error')
    } finally {
      setLoading(false)
    }
  }, [showToast])

  useEffect(() => {
    load()
  }, [load])

  // The works a job actually has, for the picker - one nobody has isn't
  // worth choosing.
  const workOptions = useMemo(() => {
    if (!data) return []
    const used = new Set(data.jobs.flatMap((j) => j.works.map((w) => w.name.toLowerCase())))
    return data.works
      .filter((w) => used.has(w.name.toLowerCase()))
      .map((w) => ({ ...w, label: w.isRetired ? `${w.name} (retired)` : w.name }))
  }, [data])

  // Start on the first work, so the view is never empty for want of a choice.
  useEffect(() => {
    if (selectedWorkId === null && workOptions.length > 0) setSelectedWorkId(workOptions[0].notifiableWorkId)
  }, [workOptions, selectedWorkId])

  const selectedWork: NotifiableWorkCatalogueEntry | undefined = workOptions.find(
    (w) => w.notifiableWorkId === selectedWorkId
  )

  const rows: JobGridRow[] = useMemo(() => {
    if (!data) return []
    const term = search.trim().toLowerCase()
    const matchesSearch = (j: NotifiableWorkJobRow) =>
      !term ||
      j.refFormatted.toLowerCase().includes(term) ||
      (j.description ?? '').toLowerCase().includes(term) ||
      (j.client ?? '').toLowerCase().includes(term)

    if (view === 'job') {
      return data.jobs.filter((j) => (showComplete || !j.isComplete) && matchesSearch(j))
    }

    if (!selectedWork) return []
    const name = selectedWork.name.toLowerCase()
    return data.jobs.flatMap((j): JobGridRow[] => {
      const work = j.works.find((w) => w.name.toLowerCase() === name)
      if (!work || (!showComplete && work.outstanding === 0) || !matchesSearch(j)) return []
      return [{ ...j, work, workDone: work.requiredDone }]
    })
  }, [data, view, showComplete, search, selectedWork])

  const sorted = useMemo(() => orderBy(rows, sort), [rows, sort])

  // Back to the first page whenever what is listed changes.
  useEffect(() => setSkip(0), [view, showComplete, search, selectedWorkId])

  // Viewable files open in a new tab; anything else downloads.
  const openFile = (file: { documentId: number; fileName: string }) =>
    getDocument(file.documentId, file.fileName).catch((error) =>
      showToast(getApiErrorDetails(error, "The file couldn't be opened.").message, 'error')
    )

  const toggleExpanded = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  // --- Cells ----------------------------------------------------------------

  const JobCell = (props: GridCellProps) => {
    const row = props.dataItem as JobGridRow
    return (
      <td {...tdProps(props)}>
        <button type='button' className='nwo-joblink' onClick={() => openJob(row.jobId)}>
          {row.refFormatted}
        </button>
        {row.isJobCompleted && <span className='nwo-jobdone'>Job completed</span>}
      </td>
    )
  }

  const DescriptionCell = (props: GridCellProps) => {
    const row = props.dataItem as JobGridRow
    return (
      <td {...tdProps(props)}>
        <div className='nwo-desc'>{row.description}</div>
        {row.client && <div className='nwo-client'>{row.client}</div>}
      </td>
    )
  }

  const WorksCell = (props: GridCellProps) => {
    const row = props.dataItem as JobGridRow
    return (
      <td {...tdProps(props)}>
        <div className='nwo-works'>
          {row.works.map((work) => {
            const key = `${row.jobId}:${work.name}`
            const open = expanded.has(key)
            const state = workState(work)
            return (
              <div key={key} className={`nwo-work${open ? ' nwo-work--open' : ''}`}>
                <button
                  type='button'
                  className={`nwo-chip nwo-chip--${state}`}
                  aria-expanded={open}
                  onClick={() => toggleExpanded(key)}
                  title={
                    state === 'none'
                      ? 'No required documents'
                      : `${work.requiredDone} of ${work.requiredCount} required documents done`
                  }
                >
                  {open ? <FaChevronDown /> : <FaChevronRight />}
                  <span className='nwo-chip__name'>{work.name}</span>
                  <span className='nwo-chip__count'>
                    {work.requiredDone}/{work.requiredCount}
                  </span>
                </button>
                {open && (
                  <ul className='nwo-docs'>
                    {work.documents.length === 0 && <li className='nwo-docs__empty'>No documents set up.</li>}
                    {work.documents.map((document) => (
                      <li key={document.notifiableWorkDocumentId} className='nwo-doc'>
                        <DocumentStatusIcon document={document} />
                        <span className='nwo-doc__name'>{document.name}</span>
                        <span className={`nwo-tag${document.isRequired ? ' nwo-tag--required' : ''}`}>
                          {document.isRequired ? 'Required' : 'Optional'}
                        </span>
                        {document.isFileUpload && document.files.length > 0 ? (
                          <span className='nwo-doc__detail nwo-doc__files'>
                            {document.files.map((file) => (
                              <button
                                key={file.documentId}
                                type='button'
                                className='nwo-doc__file'
                                title={`Open ${file.fileName}`}
                                onClick={() => openFile(file)}
                              >
                                {file.fileName}
                              </button>
                            ))}
                          </span>
                        ) : (
                          <span className='nwo-doc__detail'>
                            {document.actioned
                              ? 'Completed'
                              : document.isFileUpload
                                ? 'Not uploaded'
                                : 'Not completed'}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )
          })}
        </div>
      </td>
    )
  }

  const DoneCell = (props: GridCellProps) => {
    const work = (props.dataItem as JobGridRow).work
    if (!work) return <td {...tdProps(props)} />
    const state = workState(work)
    return (
      <td {...tdProps(props)}>
        <span className={`nwo-count nwo-count--${state}`}>
          {work.requiredDone}/{work.requiredCount}
        </span>
      </td>
    )
  }

  // One column per document of the chosen work, headed with whether it's required.
  const documentHeader = (name: string, isRequired: boolean) => (props: GridHeaderCellProps) => (
    <span className='nwo-dochead' onClick={props.onClick}>
      <span className='nwo-dochead__name' title={name}>
        {name}
      </span>
      <span className={`nwo-tag${isRequired ? ' nwo-tag--required' : ''}`}>{isRequired ? 'Required' : 'Optional'}</span>
    </span>
  )

  const documentCell = (documentId: number) => (props: GridCellProps) => {
    const document = (props.dataItem as JobGridRow).work?.documents.find(
      (d) => d.notifiableWorkDocumentId === documentId
    )
    return (
      <td {...tdProps(props, 'nwo-center')}>
        {document ? (
          <DocumentStatusIcon document={document} />
        ) : (
          <span className='nwo-mark nwo-mark--optional' title='Not part of this job'>
            <FaMinus />
          </span>
        )}
      </td>
    )
  }

  const totalLabel = (() => {
    const n = rows.length
    const noun = n === 1 ? 'job' : 'jobs'
    return showComplete ? `${n} ${noun}` : `${n} ${noun} with required documents still to do`
  })()

  return (
    <div className='nwo-page'>
      <div className='nwo-header'>
        <h1>Notifiable Works</h1>
        <p>Which jobs still need documents for their notifiable works.</p>
      </div>

      <div className='nwo-controls'>
        <div className='nwo-segment' role='group' aria-label='View'>
          <button
            type='button'
            className={view === 'job' ? 'active' : ''}
            aria-pressed={view === 'job'}
            onClick={() => setView('job')}
          >
            By job
          </button>
          <button
            type='button'
            className={view === 'work' ? 'active' : ''}
            aria-pressed={view === 'work'}
            onClick={() => setView('work')}
          >
            By notifiable work
          </button>
        </div>

        {view === 'work' && (
          <div className='nwo-field'>
            <label className='nwo-label' htmlFor='nwo-work'>
              Notifiable work
            </label>
            <DropDownList
              id='nwo-work'
              data={workOptions}
              textField='label'
              dataItemKey='notifiableWorkId'
              value={workOptions.find((w) => w.notifiableWorkId === selectedWorkId) ?? null}
              onChange={(e) => setSelectedWorkId(e.value?.notifiableWorkId ?? null)}
              style={{ width: '240px' }}
            />
          </div>
        )}

        <div className='nwo-field nwo-search'>
          <label className='nwo-label' htmlFor='nwo-search'>
            Search
          </label>
          <span className='nwo-search__box'>
            <FaSearch aria-hidden='true' />
            <input
              id='nwo-search'
              value={search}
              placeholder='Job, description or client'
              onChange={(e) => setSearch(e.target.value)}
            />
          </span>
        </div>

        <label className='nwo-toggle'>
          <Switch checked={showComplete} onChange={(e) => setShowComplete(e.value)} ariaLabel='Show complete' />
          Show complete
        </label>

        <button type='button' className='nwo-refresh' onClick={load} disabled={loading}>
          <FaSyncAlt /> {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {loadError && !data ? (
        <div className='nwo-error'>{loadError}</div>
      ) : (
        <>
          <p className='nwo-total'>{loading && !data ? 'Loading…' : totalLabel}</p>

          {view === 'work' && workOptions.length === 0 && data ? (
            <p className='nwo-empty'>No job has a notifiable work yet.</p>
          ) : (
            <Grid
              // A fresh grid per view and per chosen work: the columns differ
              // between them, and Kendo otherwise carries the old column layout
              // over, leaving headers out of line with the cells beneath.
              key={view === 'job' ? 'by-job' : `by-work-${selectedWorkId}`}
              className='nwo-grid'
              data={sorted.slice(skip, skip + PAGE_SIZE)}
              sortable
              sort={sort}
              onSortChange={(e: GridSortChangeEvent) => setSort(e.sort)}
              pageable
              pageSize={PAGE_SIZE}
              skip={skip}
              total={rows.length}
              onPageChange={(e) => setSkip(e.page.skip)}
            >
              <GridColumn key='ref' field='ref' title='Job' width='130px' cell={JobCell} />
              <GridColumn key='description' field='description' title='Description' width='280px' cell={DescriptionCell} />
              {view === 'job' ? (
                <GridColumn key='works' title='Notifiable works' sortable={false} cell={WorksCell} />
              ) : (
                [
                  <GridColumn key='done' field='workDone' title='Required done' width='130px' cell={DoneCell} />,
                  ...(selectedWork?.documents ?? []).map((d) => (
                    <GridColumn
                      key={d.notifiableWorkDocumentId}
                      title={d.name}
                      width='150px'
                      sortable={false}
                      headerCell={documentHeader(d.name, d.isRequired)}
                      cell={documentCell(d.notifiableWorkDocumentId)}
                    />
                  )),
                ]
              )}
            </Grid>
          )}
        </>
      )}
    </div>
  )
}

export default NotifiableWorks
